import { Type } from 'class-transformer';
import { FieldType } from 'src/app/settings/model/field-type';
import { Option, ValueField } from 'src/app/settings/model/value-field';
import { DeparturesByViolationViewModel } from '../components/review/departure-by-violation/departure-by-violation.component';
import { ClaimDisputeStatus, ClaimDisputeStatusUtils } from './departure-claim';
import { toDateFormat, toTime36 } from 'src/app/shared/util/date-util';

export class Departure {

    readonly id: string; // klucz odjazdu
    @Type(() => Date)
    readonly day: Date;
    readonly contract: number;
    readonly version: number; // wersja projekcji
    @Type(() => Date)
    readonly createdAt: Date; // czas utworzenia projekcji
    @Type(() => Date)
    readonly updatedAt: Date; // czas ostatniej modyfikacji projekcji

    readonly status: DepartureStatus; // status rejestracji odjazdu

    // Dane rozkładowe
    @Type(() => TimetableDeparture)
    tt: TimetableDeparture;

    // Dane rzeczywiste
    @Type(() => RealtimeDeparture)
    rt: RealtimeDeparture;

    // Dane z review
    @Type(() => Review)
    review: Review;


    reviewExist(): boolean {
        return this.review != null;
    }

    isNewerThan(departure: Departure): boolean {
        return this.version > departure.version;
    }

    getClaimsInMostFatalOrder(): ClaimDisputeStatus[] {
        if (!this.reviewExist())
            return [];
        else {
            const claims = Object.values(this.review.claims);
            return claims
                .sort((a, b) => ClaimDisputeStatusUtils.disputesOrder.indexOf(a as string) - ClaimDisputeStatusUtils.disputesOrder.indexOf(b as string))
                .map(v => ClaimDisputeStatus[v]);
        }
    }

    getIDViolations(): number[] {
        return this.reviewExist() ? Object.keys(this.review.claims).map(v => parseInt(v)) : [];
    }

    removeViolation(idViolation: number): void {
        delete this.review?.claims[idViolation];
    }

    addViolation(idViolation: number, indisputable: boolean = false): void {
        if (!this.reviewExist())
            this.review = Review.createNew();
        this.review.claims[idViolation] = indisputable ? ClaimDisputeStatus.INDISPUTABLE : ClaimDisputeStatus.NONE;
    }

    toDepartureReviewIDVersion(): DepartureIDVersion {
        return new DepartureIDVersion(this.id, this.review?.version || 0);
    }

    canAddViolations(idViolation: number, conflictsViolationsIDs: number[]): boolean {
        const violationsIds = this.getIDViolations();
        return !violationsIds.includes(idViolation) && violationsIds.every(v => !conflictsViolationsIDs.includes(v));
    }

    toDepartureWithViolationView(idViolation: number): DeparturesByViolationViewModel {
        return new DeparturesByViolationViewModel(
            this.id, this.review.version, idViolation, this.rt?.vh,
            toDateFormat(this.day), this.tt.task, this.tt.ln, this.tt.dir,
            toTime36(this.crsFrom, this.day) + ' — ' + toTime36(this.crsTill, this.day),
            toTime36(this.tt.spTime, this.day),
            `${this.tt.spName} (${this.tt.spCode})`,
            null, null, null, null
        );
    }

    createNewIdViolationDepartureForReclamations(): DeparturesByViolationViewModel[] {
        return this.getIDViolations()
            .filter(v => ClaimDisputeStatusUtils.possibleToCreateReclamation.includes(this.review.claims[v]))
            .reduce((acc, idViolation) => {
                acc.push(this.toDepartureWithViolationView(idViolation));
                return acc;
            }, [] as DeparturesByViolationViewModel[]);
    }

    addClaim(idViolation: number, newClaim: ClaimDisputeStatus): void {
        const claim = this.review.claims[idViolation];
        if (claim === ClaimDisputeStatus.NONE)
            this.review.claims[idViolation] = newClaim;
    }

    isPending(): boolean {
        return this.status == DepartureStatus.PENDING;
    }

    isFirstOrLastStopOnCourse(): boolean {
        return this.spPosition !== StopPosition.MIDDLE;
    }

    get dt(): string { return this.tt.dt; }
    get ln(): string { return this.tt.ln; }
    get dir(): string { return this.tt.dir; }
    get brigade(): string { return this.tt.brigade; }
    get task(): string { return this.tt.task; }

    get crs(): string { return this.tt.crs; }
    get crsDist(): string { return `${this.tt.crsDist}m`; }
    get crsFrom(): Date { return this.tt.crsFrom; }
    get crsTill(): Date { return this.tt.crsTill; }
    get crsHeadway(): string { return `${this.tt.crsHeadway}min`; }
    get crsVhCategory(): string { return this.tt.crsVhCategory ?? '⁉️'; }
    get crsSpCount(): number { return this.tt.crsSpCount; }

    get spIndex(): string { return `${this.tt.spIndex + 1} z ${this.tt.crsSpCount}`; }
    get spTime(): Date { return this.tt.spTime; }
    get spPosition(): StopPosition { return this.tt.spPosition; }
    get spCode(): string { return this.tt.spCode; }
    get spName(): string { return this.tt.spName; }
    get spOnDemand(): boolean { return this.tt.spOnDemand; }
    get spCtrlPoint(): boolean { return this.tt.spCtrlPoint; }
    get spDistPrev(): string { return `${this.tt.spDistPrev}m`; }
    get spDistOrigin(): string { return `${this.tt.spDistOrigin}m`; }
    get spTariffZone(): string { return this.tt.spTariffZone; }
    get spGps(): string | null {
        const { spLat, spLon } = this.tt;
        if (spLat == null || spLon == null) return null;

        const latDir = spLat >= 0 ? 'N' : 'S';
        const lonDir = spLon >= 0 ? 'E' : 'W';

        return `${Math.abs(spLat).toFixed(4)}° ${latDir} ${Math.abs(spLon).toFixed(4)}° ${lonDir}`;
    }

    get vhTimestamp(): Date | null { return this.rt?.vhTimestamp ?? null; }
    get vh(): string | null {
        if (!this.rt?.vh) return null;
        const manReportedVehicleSymbol = this.rt?.vhUserReported ? '✍️' : '';
        const replacedVehicle = this.rt.replacedVh ? ` ⬅️ ${this.rt.replacedVh}` : '';
        return `${this.rt.vh}${manReportedVehicleSymbol}${replacedVehicle}`;
    }
    // Sam numer, bez ozdobników z komórki - po nim filtruje się i jego wysyła się na serwer.
    get vhNumber(): string | null { return this.rt?.vh ?? null; }
    get vhRole(): VehicleRole | null { return this.rt?.vhRole ?? null; }
    get vhYear(): number | null { return this.rt?.vhYear ?? null; }
    get vhCategory(): string | null { return this.rt?.vhCategory ?? null; }
    get vhQualification(): VehicleQualification | null { return this.rt?.vhQualification ?? null; }

    get dr(): string | null { return this.rt?.dr ?? null; }
    get drName(): string | null { return this.rt?.drName ?? null; }

    get znEnter(): Date | null { return this.rt?.znEnter ?? null; }
    get znLeave(): Date | null { return this.rt?.znLeave ?? null; }
    get izDoorOpen(): Date | null { return this.rt?.izDoorOpen ?? null; }
    get izDoorClose(): Date | null { return this.rt?.izDoorClose ?? null; }
    get ozDoorOpen(): Date | null { return this.rt?.ozDoorOpen ?? null; }
    get ozDoorClose(): Date | null { return this.rt?.ozDoorClose ?? null; }

    get doorBtnEnable(): Date | null { return this.rt?.doorBtnEnable ?? null; }
    get doorBtnDisable(): Date | null { return this.rt?.doorBtnDisable ?? null; }
    get stopBtnPressed(): boolean | null { return this.rt?.stopBtnPressed ?? null; }
    get wheelchairBtnPressed(): boolean | null { return this.rt?.wheelchairBtnPressed ?? null; }

    get tempInside(): string | null { return this.getRatedTemperatureDisplay(this.rt?.tempInside ?? null); }
    get tempOutside(): string | null { return this.getTemperatureDisplay(this.rt?.tempOutside, 1); }

    get haltTime(): Date | null { return this.rt?.assessed?.haltTime ?? null; }
    get departureTime(): Date | null { return this.rt?.assessed?.departureTime ?? null; }
    get passthroughTime(): Date | null { return this.rt?.assessed?.passthroughTime ?? null; }

    get delay(): string | null { return this.getDelayDisplay(this.rt?.assessed?.delay ?? null); }

    get claims(): ClaimsMap { return this.review?.claims ?? null; }

    get passengersRawTotal(): number | null { return this.rt?.passengers?.raw?.total ?? null; }
    get passengersRawPerDoorCounts(): string | null {
        const perDoorCounts = this.getPassengerPerDoorCountsDisplay(this.rt?.passengers?.raw?.door ?? null);
        if (!perDoorCounts) return null;
        return perDoorCounts + ` | Δ=${this.rt?.passengers?.raw?.delta ?? '∞'}`;
    }

    get passengersCorrectedTotal(): number | null { return this.rt?.passengers?.corrected?.total ?? null; }
    get passengersCorrectedPerDoorCounts(): string | null {
        // FIXME: Mamy duplikat passengersRawPerDoorCounts - posprzątać.
        const perDoorCounts = this.getPassengerPerDoorCountsDisplay(this.rt?.passengers?.corrected?.door ?? null);
        if (!perDoorCounts) return null;
        return `${perDoorCounts} | Δ=${this.rt?.passengers?.corrected?.delta ?? '∞'}`;
    }

    static getValueFields(contractOptions: Option[], violationOptions: Option[], disputeStatusOptions?: Option[]): ValueField[] {
        let i = 0;
        const contractOptionsWithInterrobangForZero = [...contractOptions, { key: '0', value: '⁉️', isHtml: false }];
        return [
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-timestamp',
                name: DepartureFields.RT_VEHICLE_TIMESTAMP,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.day',
                name: DepartureFields.DAY, type: FieldType.DATE, canFilter: true, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.contract',
                name: DepartureFields.CONTRACT, type: FieldType.CONTRACT,
                canFilter: true, canGroup: true, colNumber: ++i,
                options: contractOptionsWithInterrobangForZero
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.line',
                name: DepartureFields.COURSE_LINE, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.direction',
                name: DepartureFields.COURSE_DIRECTION, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.course',
                name: DepartureFields.COURSE_NAME, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.claims',
                name: DepartureFields.CLAIMS, type: FieldType.VIOLATIONS_MARKER,
                canFilter: true, canGroup: false, colNumber: ++i,
                options: violationOptions,
                filterEmptyOptionLabel: 'sakp.departures.columns.no-claims',
                rowValueExtractor: (row: Departure) => Object.keys((row as any).review?.claims ?? {})
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.violation-with-status',
                name: DepartureFields.VIOLATION_WITH_STATUS, type: FieldType.VIOLATION_WITH_STATUS,
                canFilter: true, canGroup: false, visible: false, colNumber: ++i, permanentlyHidden: true,
                rowValueExtractor: (row: Departure) => Object.entries((row as any).review?.claims ?? {}).map(([id, status]) => `${id};${status}`)
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-time',
                name: DepartureFields.STOP_TIME, type: FieldType.DATETIME,
                canFilter: true, canGroup: true, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-on-demand',
                name: DepartureFields.STOP_ON_DEMAND, type: FieldType.BOOLEAN,
                canFilter: true, canGroup: true, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-control-point',
                name: DepartureFields.STOP_CONTROL_POINT, type: FieldType.BOOLEAN,
                canFilter: true, canGroup: true, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-delay',
                name: DepartureFields.RT_CALCULATED_DELAY,
                type: FieldType.INTEGER, canFilter: true, canGroup: false, colNumber: ++i,
                rowValueExtractor: (row: Departure) => (row as any).rt?.assessed?.delay?.seconds
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-index',
                name: DepartureFields.STOP_INDEX,
                type: FieldType.INTEGER, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-code',
                name: DepartureFields.STOP_CODE, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-name',
                name: DepartureFields.STOP_NAME, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-distance-from-previous',
                name: DepartureFields.STOP_DISTANCE_FROM_PREVIOUS,
                type: FieldType.INTEGER, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.zone-enter-time',
                name: DepartureFields.RT_ZONE_ENTER_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.zone-leave-time',
                name: DepartureFields.RT_ZONE_LEAVE_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.in-zone-door-open-time',
                name: DepartureFields.RT_IN_ZONE_DOOR_OPEN_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.in-zone-door-close-time',
                name: DepartureFields.RT_IN_ZONE_DOOR_CLOSE_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.outside-zone-door-open-time',
                name: DepartureFields.RT_OUTSIDE_ZONE_DOOR_OPEN_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.outside-zone-door-close-time',
                name: DepartureFields.RT_OUTSIDE_ZONE_DOOR_CLOSE_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.task',
                name: DepartureFields.COURSE_TASK, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-side-number',
                name: DepartureFields.RT_VEHICLE_SIDE_NUMBER, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true,
                filterEmptyOptionLabel: 'column-filter.no-values',
                rawValueFieldName: 'vhNumber',
                rowValueExtractor: (row: Departure) => row.vhNumber
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.course-vehicle-category',
                name: DepartureFields.COURSE_VEHICLE_CATEGORY, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-category',
                name: DepartureFields.RT_VEHICLE_CATEGORY, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true,
                filterEmptyOptionLabel: 'column-filter.no-values'
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-halt-time',
                name: DepartureFields.RT_CALCULATED_HALT_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-departure-time',
                name: DepartureFields.RT_CALCULATED_DEPARTURE_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-zone-passthrough-time',
                name: DepartureFields.RT_CALCULATED_PASSTHROUGH_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.driver-code',
                name: DepartureFields.RT_DRIVER_CODE,
                type: FieldType.TEXT, canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true,
                filterEmptyOptionLabel: 'column-filter.no-values'
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.driver-name',
                name: DepartureFields.RT_DRIVER_NAME,
                type: FieldType.TEXT, canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true,
                filterEmptyOptionLabel: 'column-filter.no-values'
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.temperature-inside',
                name: DepartureFields.RT_TEMP_INSIDE,
                type: FieldType.FLOAT, canFilter: true, canGroup: false, colNumber: ++i,
                rowValueExtractor: (row: Departure) => (row as any).rt?.tempInside?.value
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.temperature-outside',
                name: DepartureFields.RT_TEMP_OUTSIDE,
                type: FieldType.FLOAT, canFilter: true, canGroup: false, colNumber: ++i,
                rowValueExtractor: (row: Departure) => (row as any).rt?.tempOutside
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-button-pressed',
                name: DepartureFields.RT_STOP_BUTTON_PRESSED,
                type: FieldType.BOOLEAN, canFilter: true, canGroup: true, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.wheelchair-button-pressed',
                name: DepartureFields.RT_WHEELCHAIR_BUTTON_PRESSED,
                type: FieldType.BOOLEAN, canFilter: true, canGroup: true, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.door-button-enable-time',
                name: DepartureFields.RT_DOOR_BUTTON_ENABLE_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.door-button-disable-time',
                name: DepartureFields.RT_DOOR_BUTTON_DISABLE_TIME,
                type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i
            }),
            // Zliczenia pasażerów z podziałem na drzwi; liczniki surowe (właściwie "wstępnie przetworzone") i skorygowane.
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.passengers-raw-total',
                name: DepartureFields.RT_PASSENGER_COUNTS_RAW_TOTAL,
                type: FieldType.INTEGER, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.passengers-raw-per-door-counts',
                name: DepartureFields.RT_PASSENGER_COUNTS_RAW_PER_DOOR,
                type: FieldType.TEXT, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.passengers-corrected-total',
                name: DepartureFields.RT_PASSENGER_COUNTS_CORRECTED_TOTAL,
                type: FieldType.INTEGER, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.passengers-corrected-per-door-counts',
                name: DepartureFields.RT_PASSENGER_COUNTS_CORRECTED_PER_DOOR,
                type: FieldType.TEXT, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.status',
                name: DepartureFields.STATUS, type: FieldType.TEXT, canFilter: true, canGroup: true,
                colNumber: ++i,
                options: Object.keys(DepartureStatus).map(key => ({ key, value: 'sakp.common.enums.departure-status.' + DepartureStatus[key] }))
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-gps-location',
                name: DepartureFields.STOP_GPS_LOCATION,
                type: FieldType.TEXT, canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-tariff-zone',
                name: DepartureFields.STOP_TARIFF_ZONE,
                type: FieldType.TEXT, canFilter: true, canGroup: true, colNumber: ++i, filterOptionsFromData: true
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.day-type',
                name: DepartureFields.COURSE_DAY_TYPE, type: FieldType.TEXT,
                canFilter: true, canGroup: true, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-distance-from-origin',
                name: DepartureFields.STOP_DISTANCE_FROM_ORIGIN, type: FieldType.INTEGER,
                canFilter: false, canGroup: false, colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.course-begin-time',
                name: DepartureFields.COURSE_BEGIN_TIME, type: FieldType.DATETIME,
                canFilter: true, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.course-end-time',
                name: DepartureFields.COURSE_END_TIME, type: FieldType.DATETIME,
                canFilter: true, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.stop-position',
                name: DepartureFields.STOP_POSITION, type: FieldType.ENUM, canFilter: true, canGroup: true,
                colNumber: ++i,
                options: Object.keys(StopPosition).map(key => ({ key, value: 'sakp.common.enums.stop-position.' + StopPosition[key] }))
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.headway',
                name: DepartureFields.COURSE_HEADWAY, type: FieldType.INTEGER,
                canFilter: false, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.course-distance',
                name: DepartureFields.COURSE_DISTANCE, type: FieldType.FLOAT,
                canFilter: false, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-role',
                name: DepartureFields.RT_VEHICLE_ROLE, type: FieldType.ENUM, canFilter: true, canGroup: true,
                colNumber: ++i,
                options: Object.keys(VehicleRole).map(key => ({ key, value: 'sakp.common.enums.vehicle-role.' + VehicleRole[key] }))
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-year',
                name: DepartureFields.RT_VEHICLE_YEAR, type: FieldType.INTEGER, canFilter: true, canGroup: true,
                colNumber: ++i
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.vehicle-qualification',
                name: DepartureFields.RT_VEHICLE_QUALIFICATION, type: FieldType.ENUM, canFilter: true, canGroup: true,
                colNumber: ++i,
                options: Object.keys(VehicleQualification).map(key => ({ key, value: 'sakp.common.enums.vehicle-qualification.' + VehicleQualification[key] }))
            }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.created-at',
                name: DepartureFields.CREATED_AT, type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.version',
                name: DepartureFields.VERSION, type: FieldType.INTEGER, canFilter: false, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.updated-at',
                name: DepartureFields.UPDATED_AT, type: FieldType.DATETIME, canFilter: true, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.brigade',
                name: DepartureFields.COURSE_BRIGADE, type: FieldType.TEXT, canFilter: true, canGroup: true, colNumber: ++i,
                filterOptionsFromData: true }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.id',
                name: DepartureFields.ID, type: FieldType.TEXT, primaryKey: true, canFilter: false, canGroup: false, colNumber: ++i }),
            Object.assign(new ValueField(), {
                title: 'sakp.departures.columns.disputes-status',
                name: DepartureFields.DISPUTES_STATUS, type: FieldType.DISPUTE_STATUS_FILTER, canFilter: true, canGroup: true, visible: false,
                colNumber: ++i, permanentlyHidden: true,
                options: disputeStatusOptions ?? ClaimDisputeStatusUtils.filterable.map(key => ({key, value: 'sakp.common.enums.dispute-status.' + key})),
                rowValueExtractor: (row: Departure) => Object.values((row as any).review?.claims ?? {})
            })
        ];
    }

    private getPassengerPerDoorCountsDisplay(value: Record<number, { enter: number | null; exit: number | null }>): string | null {
        if (!value) return null;
        return Object.keys(value).map(key => {
            const door = Number(key);
            const counts = value[door];
            return `${String.fromCharCode(0x30 + door) + '\uFE0F\u20E3'} ${counts.enter}↑ ${counts.exit}↓`;
        }).join(' ');
    }

    private getTemperatureDisplay(value: number, fixed: number): string | null {
        if (value === undefined || value === null) return null;
        return Number.isFinite(value) ? `${value.toFixed(fixed)}°C` : String(value);
    }

    private getRatedTemperatureDisplay(temp: RatedTemperatureValue | null): string | null{
        if (!temp) return null;
        return `${this.getTemperatureDisplay(temp.value, 1)} ${this.getTemperatureRatingSymbol(temp.rating)}`;
    }

    private getTemperatureRatingSymbol(rating: TemperatureAcceptability): string {
        switch (rating) {
            case TemperatureAcceptability.ACCEPTABLE:
                return '';
            case TemperatureAcceptability.COLD:
            case TemperatureAcceptability.TOO_COLD:
                return '❄️';
            case TemperatureAcceptability.HOT:
            case TemperatureAcceptability.TOO_HOT:
                return '🔥';
            case TemperatureAcceptability.INCALCULABLE:
                return '❓';
            default:
                return '';
        }
    }

    private getDelayDisplay(delay: DelayValue | null): string | null{
        if (!delay) return null;
        const symbol = this.getDelaySymbol(delay.timeliness);

        const sign = delay.seconds < 0 ? '+' : delay.seconds > 0 ? '-' : '';
        const seconds = Math.abs(delay.seconds);

        const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');

        return `${sign}${hours}:${minutes}:${secs}⏱️ ${symbol}`;
    }

    private getDelaySymbol(timeliness: Timeliness): string {
        switch (timeliness) {
            case Timeliness.ON_TIME:
                return '';
            case Timeliness.AHEAD_OF_TIME:
                return '🟫';
            case Timeliness.DELAYED:
                return '🟨';
            case Timeliness.DELAYED_OVER_INTERVAL:
                return '🟧';
            case Timeliness.DELAYED_OVER_LIMIT:
                return '🟥';
            default:
                return '❓';
        }
    }

}

export class TimetableDeparture {
    readonly brigade: string;
    readonly task: string;
    readonly dt: string;
    readonly ln: string;
    readonly dir: string;
    readonly crs: string;
    @Type(() => Date)
    readonly crsFrom: Date;
    @Type(() => Date)
    readonly crsTill: Date;
    readonly crsHeadway: number;
    readonly crsVhCategory: string | null;
    readonly crsDist: number;
    readonly crsSpCount: number;
    readonly spIndex: number;
    readonly spPosition: StopPosition;
    @Type(() => Date)
    readonly spTime: Date;
    readonly spCode: string;
    readonly spName: string;
    readonly spOnDemand: boolean;
    readonly spCtrlPoint: boolean;
    readonly spDistPrev: number;
    readonly spDistOrigin: number;
    readonly spTariffZone: string;
    readonly spLat: number | null = null;
    readonly spLon: number | null = null;
}

export class RealtimeDeparture {
    @Type(() => Date)
    readonly vhTimestamp: Date;
    readonly replacedVh: string | null;
    readonly vh: string;
    readonly vhRole: VehicleRole; // "rodzaj" pojazdu
    readonly vhYear: number | null;
    readonly vhCategory: string | null; // "typ" pojazdu
    readonly vhQualification: VehicleQualification | null;
    readonly vhUserReported: boolean | null;
    readonly dr: string;
    readonly drName: string;
    @Type(() => Date)
    readonly znEnter: Date;
    @Type(() => Date)
    readonly znLeave: Date;
    @Type(() => Date)
    readonly izDoorOpen: Date;
    @Type(() => Date)
    readonly izDoorClose: Date;
    @Type(() => Date)
    readonly ozDoorOpen: Date;
    @Type(() => Date)
    readonly ozDoorClose: Date;
    readonly stopBtnPressed: boolean;
    readonly wheelchairBtnPressed: boolean;
    @Type(() => Date)
    readonly doorBtnEnable: Date;
    @Type(() => Date)
    readonly doorBtnDisable: Date;
    readonly tempInside: RatedTemperatureValue;
    readonly tempOutside: number;
    readonly assessed: VehicleStopAssessment;
    readonly passengers: PassengerCounts | null;

    readonly version: number;
}

export class PassengerCounts {
    readonly raw: { total: number | null; delta: number | null; door: Record<number, { enter: number | null; exit: number | null }> };
    readonly corrected: { total: number; delta: number | null; door: Record<number, { enter: number; exit: number }>} | null;
}

export class VehicleStopAssessment {
    readonly served: boolean;
    @Type(() => Date)
    readonly departureTime: Date;
    @Type(() => Date)
    readonly haltTime: Date;
    @Type(() => Date)
    readonly passthroughTime: Date;
    readonly delay: DelayValue;
}

export class DelayValue {
    readonly seconds: number;
    readonly timeliness: Timeliness;
}

export class RatedTemperatureValue {
    readonly value: number;
    readonly rating: TemperatureAcceptability;
}

export class RatedVehicleSizeValue {
    readonly value: VehicleSize;
    readonly rating: SizeRating;
}

export class Review {
    readonly version: number;
    // TODO: przemyśleć jeszcze, czy aby na pewno nie chcemy kolumny z wyjaśnieniem.
    readonly claims: Record<number, ClaimDisputeStatus>;

    static createNew(): Review {
        return Object.assign(new Review(), { version: 0, claims: {} });
    }
}

export class DepartureIDVersion {
    constructor(public readonly id: string, public readonly version: number) { }
}


export enum DepartureStatus {
    PENDING = 'PENDING',
    UNREGISTERED = 'UNREGISTERED',
    REGISTERED = 'REGISTERED'
}

enum VehicleRole {
    NOT_SPECIFIED = 'NOT_SPECIFIED',
    PRIMARY = 'PRIMARY',
    REPLACEMENT = 'REPLACEMENT',
    SUPPLEMENTARY = 'SUPPLEMENTARY',
    RESERVE = 'RESERVE'
}

enum VehicleSize {
    NOT_SPECIFIED = 'NOT_SPECIFIED', MINI = 'MINI', MIDI = 'MIDI', SOLO = 'SOLO', MEGA = 'MEGA'
}

export enum StopPosition {
    FIRST = 'FIRST', MIDDLE = 'MIDDLE', LAST = 'LAST'
}

// enum VehicleDrive {
//     NOT_SPECIFIED = 'NOT_SPECIFIED', CONVENTIONAL = 'CONVENTIONAL', ZERO_EMISSION = 'ZERO_EMISSION'
// }

export enum VehicleQualification {
    PRIMARY = 'PRIMARY',
    REPLACEMENT = 'REPLACEMENT',
    INVALID = 'INVALID'
}

enum Timeliness {
    AHEAD_OF_TIME = 'AHEAD_OF_TIME',
    ON_TIME = 'ON_TIME',
    DELAYED = 'DELAYED',
    DELAYED_OVER_INTERVAL = 'DELAYED_OVER_INTERVAL',
    DELAYED_OVER_LIMIT = 'DELAYED_OVER_LIMIT'
}

enum TemperatureAcceptability {
    ACCEPTABLE = 'ACCEPTABLE',
    COLD = 'COLD',
    TOO_COLD = 'TOO_COLD',
    HOT = 'HOT',
    TOO_HOT = 'TOO_HOT',
    INCALCULABLE = 'INCALCULABLE'
}

enum SizeRating {
    AS_REQUIRED = 'AS_REQUIRED',
    BIGGER_THAN_REQUIRED = 'BIGGER_THAN_REQUIRED',
    SMALLER_THAN_REQUIRED = 'SMALLER_THAN_REQUIRED',
    INCALCULABLE = 'INCALCULABLE',
}

export enum DepartureFields {
    ID = 'id',
    DAY = 'day',
    CONTRACT = 'contract',
    VERSION = 'version',
    CREATED_AT = 'createdAt',
    UPDATED_AT = 'updatedAt',
    STATUS = 'status',

    // tt
    COURSE_BRIGADE = 'brigade',
    COURSE_TASK = 'task',
    COURSE_DAY_TYPE = 'dt',
    COURSE_LINE = 'ln',
    COURSE_DIRECTION = 'dir',
    COURSE_NAME = 'crs',
    COURSE_BEGIN_TIME = 'crsFrom',
    COURSE_END_TIME = 'crsTill',
    COURSE_HEADWAY = 'crsHeadway',
    COURSE_VEHICLE_CATEGORY = 'crsVhCategory',
    COURSE_DISTANCE = 'crsDist',
    COURSE_STOP_COUNT = 'crsSpCount',
    STOP_INDEX = 'spIndex',
    STOP_POSITION = 'spPosition',
    STOP_TIME = 'spTime',
    STOP_CODE = 'spCode',
    STOP_NAME = 'spName',
    STOP_ON_DEMAND = 'spOnDemand',
    STOP_CONTROL_POINT = 'spCtrlPoint',
    STOP_DISTANCE_FROM_PREVIOUS = 'spDistPrev',
    STOP_DISTANCE_FROM_ORIGIN = 'spDistOrigin',
    STOP_TARIFF_ZONE = 'spTariffZone',
    STOP_GPS_LOCATION = 'spGps',

    // rt
    RT_VEHICLE_TIMESTAMP = 'vhTimestamp',
    RT_VEHICLE_SIDE_NUMBER = 'vh',
    RT_VEHICLE_ROLE = 'vhRole',
    RT_VEHICLE_YEAR = 'vhYear',
    RT_VEHICLE_CATEGORY = 'vhCategory',
    RT_VEHICLE_QUALIFICATION = 'vhQualification',

    RT_DRIVER_CODE = 'dr',
    RT_DRIVER_NAME = 'drName',

    RT_ZONE_ENTER_TIME = 'znEnter',
    RT_ZONE_LEAVE_TIME = 'znLeave',
    RT_IN_ZONE_DOOR_OPEN_TIME = 'izDoorOpen',
    RT_IN_ZONE_DOOR_CLOSE_TIME = 'izDoorClose',
    RT_OUTSIDE_ZONE_DOOR_OPEN_TIME = 'ozDoorOpen',
    RT_OUTSIDE_ZONE_DOOR_CLOSE_TIME = 'ozDoorClose',

    RT_TEMP_INSIDE = 'tempInside',
    RT_TEMP_OUTSIDE = 'tempOutside',

    RT_STOP_BUTTON_PRESSED = 'stopBtnPressed',
    RT_WHEELCHAIR_BUTTON_PRESSED = 'wheelchairBtnPressed',
    RT_DOOR_BUTTON_ENABLE_TIME = 'doorBtnEnable',
    RT_DOOR_BUTTON_DISABLE_TIME = 'doorBtnDisable',

    RT_CALCULATED_DELAY = 'delay',
    RT_CALCULATED_HALT_TIME = 'haltTime',
    RT_CALCULATED_DEPARTURE_TIME = 'departureTime',
    RT_CALCULATED_PASSTHROUGH_TIME = 'passthroughTime',

    RT_PASSENGER_COUNTS_RAW_TOTAL = 'passengersRawTotal',
    RT_PASSENGER_COUNTS_RAW_PER_DOOR = 'passengersRawPerDoorCounts',
    RT_PASSENGER_COUNTS_CORRECTED_TOTAL = 'passengersCorrectedTotal',
    RT_PASSENGER_COUNTS_CORRECTED_PER_DOOR = 'passengersCorrectedPerDoorCounts',

    // review
    CLAIMS = 'claims',
    DISPUTES_STATUS = 'disputesStatus',
    VIOLATION_WITH_STATUS = 'violationWithStatus',
}

type ClaimsMap = Record<number, ClaimDisputeStatus>;
